import router from "@/router";

// Routes that should NEVER auto-redirect on 401/403. The watch-party route is
// public per spec; /locked and /unlock-error are where we'd redirect TO, so
// redirecting from them creates a loop. /remote and /remote/:sessionId are
// public for the phone-as-remote flow — the phone may not have an unlock
// cookie at all, and we surface the "unlock first" hint inline rather than
// bouncing them out to /locked.
const PUBLIC_ROUTE_NAMES = new Set([
  "locked",
  "unlock-error",
  "unlock-device-limit",
  "watch-party",
  "remote",
  "remote-home",
  // Feature 75: a share viewer has no device cookie by design, so background
  // calls that need one (e.g. the device-handoff heartbeat) legitimately 401.
  // Bouncing them to /locked would eject them from the video they were
  // deliberately given access to.
  "share",
]);

export class ApiError extends Error {
  constructor(status, body, message) {
    super(message || `API error ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/**
 * Thin `fetch` wrapper for gated SPA endpoints.
 *
 * - Always sends cookies (same-origin in prod; Vite dev server proxies /api → :3001).
 * - Default Accept: application/json so the server logout handler returns JSON
 *   instead of a 302 redirect.
 * - `options.json` auto-serializes and sets Content-Type: application/json.
 * - Parses JSON responses automatically. Non-2xx throws `ApiError`.
 * - On 401/403: flips the auth store to 'locked' and redirects to /locked
 *   (unless the current route is already one of the public routes).
 *
 * @param {string} url
 * @param {RequestInit & { json?: any }} [options]
 */
export async function apiFetch(url, options = {}) {
  const { json, headers, ...rest } = options;
  const init = {
    credentials: "include",
    ...rest,
    headers: {
      Accept: "application/json",
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(headers || {}),
    },
    body: json !== undefined ? JSON.stringify(json) : options.body,
  };

  const res = await fetch(url, init);

  let parsed = null;
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    try {
      parsed = await res.json();
    } catch {
      parsed = null;
    }
  } else if (res.status !== 204) {
    try {
      parsed = await res.text();
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    // 401 = auth is invalid (no cookie / revoked / expired). Flip the
    // store to locked and bounce to /locked so the user can re-auth.
    //
    // 403 was previously treated the same way. With granular permissions
    // that's wrong: 403 from a permission-gated endpoint (live-tv,
    // torbox, etc.) just means "this endpoint isn't yours". The session
    // itself is still valid -- the rest of the app should keep working.
    // Bouncing to /locked dead-ended users out of /library just because
    // a backgrounded /api/live/playlists request returned 403.
    if (res.status === 401) {
      try {
        // eslint-disable-next-line import/no-cycle
        const { useAuthStore } = await import("@/stores/auth");
        const authStore = useAuthStore();
        authStore.markLocked();
      } catch {
        // Pinia not ready (shouldn't happen in practice) — still redirect.
      }
      const currentName = router.currentRoute.value?.name;
      if (!PUBLIC_ROUTE_NAMES.has(currentName)) {
        try {
          router.push({ name: "locked" });
        } catch {
          // Router might not have the 'locked' route yet. Swallow.
        }
      }
    }
    throw new ApiError(res.status, parsed);
  }

  return parsed;
}
